import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDollarSign, ClipboardList, Clock } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDaContingencia } from '@/componentes/CampoDaContingencia';
import { CampoDeCompetencia, CampoDeData } from '@/componentes/CampoDeData';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { Medida } from '@/componentes/Medida';
import { rotuloDaEscolha } from '@/componentes/rotuloDaEscolha';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { contingenciaValida, EFEITO_DA_CONTINGENCIA, recusaDoMestre } from '@/lib/contingencia';
import { formatarCompetencia, formatarDecimal, formatarInteiro, formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useCadastro } from '@/modulos/cadastros/consultas';
import { margemEscrita } from '@/modulos/painel/gestor';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, fatiarPagina } from '@/modulos/painel/painel';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { useSessao } from '@/modulos/sessao/consultas';

import { api, type LinhaDeMargem, type Medicao } from './api';
import { contratoAMedir, deOndeVemONumero, estadoDaFaixa, porqueNaoDa, situacaoEscrita, totaisDaReceita, valorDoCampo } from './receita';

/**
 * MÓDULO 14 — contratos, medições e receita. A primeira tela do ciclo 2.
 *
 * ## O risco do módulo é de contagem, não de conta
 *
 * O v6 escreve em uma linha: *"considerar medição, nota e recebimento como TRÊS
 * receitas"*. São três eventos do mesmo dinheiro, cada um com data própria — o
 * que foi aceito, o que foi cobrado, o que entrou. Somados, triplicam o
 * faturamento, e o relatório continua fechando.
 *
 * Por isso a tela tem UM número de receita, e ele vem da medição aprovada. O
 * documento e o recebimento aparecem como ESTADO da mesma linha — "NF-51 ·
 * recebido em 10/09" —, nunca como valor novo numa coluna que alguém somaria.
 *
 * ## A margem por contrato (ferramenta 4, 0096)
 *
 * A margem é a receita aprovada menos o CUSTO TOTAL do contrato — direto,
 * depreciação e custo indireto rateado, com a máquina de vários contratos
 * dividida pela hora —, pela mesma conta da Visão do gestor. Ela só se chama
 * "Margem" quando nada ficou fora na competência; senão a célula diz
 * "parcial", e a rota manda o porquê escrito (`limitacao`), que a tela imprime
 * como veio: o v6 avisa do risco de "chamar custo parcial de margem final".
 *
 * ## Montar não é aprovar
 *
 * "Montar medição" é a única ação em laranja: é o que a tela existe para
 * provocar. Ela MONTA uma proposta a partir da alocação, das horas que a
 * conciliação reconheceu e da regra vigente do contrato. Aprovar está no menu
 * da linha e é outra licença — quem monta propõe, outra pessoa aceita.
 */

/** O primeiro dia do mês passado: a competência que quase sempre se fecha. */
function competenciaPadrao(): string {
  const hoje = new Date();
  const mes = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${String(mes.getFullYear())}-${String(mes.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Hoje no fuso da operação (`diaDaOperacao`), e não no fuso do aparelho. */
function hojeIso(): string {
  return diaDaOperacao();
}

type Pendente =
  | { readonly tipo: 'cancelar'; readonly medicao: Medicao }
  | { readonly tipo: 'emitir'; readonly medicao: Medicao }
  | { readonly tipo: 'receber'; readonly medicao: Medicao };

export function PaginaReceita() {
  const base = useId();
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = useMemo(() => sessao.data?.permissoes ?? [], [sessao.data]);
  /* Cada ação só para quem tem a licença dela (prova do marco, 07/10/2026):
     antes elas apareciam desligadas, com a licença no motivo. */
  const podeMontar = permissoes.includes('medicao.gravar');
  const podeAprovarMedicao = permissoes.includes('medicao.aprovar');
  const podeEmitir = permissoes.includes('documento.emitir');
  const podeReceber = permissoes.includes('documento.receber');
  /* 0109: com o ERP mestre da cobrança, quem tem a licença declara a contingência aqui. */
  const podeContingencia = permissoes.includes('integracao.contingencia');

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);
  const [montando, setMontando] = useState(false);
  const [contratoId, setContratoId] = useState('');
  /* O contrato proposto (`contratoAMedir`) até a pessoa escolher outro. */
  const [contratoTocado, setContratoTocado] = useState(false);
  const [competencia, setCompetencia] = useState(competenciaPadrao);
  const [pendente, setPendente] = useState<Pendente | null>(null);
  const [motivo, setMotivo] = useState('');
  const [numero, setNumero] = useState('');
  const [quando, setQuando] = useState(hojeIso);
  const [valorRecebido, setValorRecebido] = useState('');
  const [contingencia, setContingencia] = useState('');

  const medicoes = useQuery({ queryKey: ['receita', 'medicoes'], queryFn: api.medicoes });
  const margem = useQuery({ queryKey: ['receita', 'margem'], queryFn: api.margem });
  const paginaDaMargem = usePaginacao(margem.data?.linhas ?? []);
  const linhas = useMemo(() => medicoes.data?.medicoes ?? [], [medicoes.data]);
  const totais = useMemo(() => totaisDaReceita(linhas), [linhas]);
  const faixa = estadoDaFaixa(medicoes);
  const fatia = fatiarPagina(linhas, pagina, porPagina);
  /* O contrato da medição sai da lista: a janela pedia o identificador interno
     (achado no QAS, 20/10/2026). Os contratos em uso do cadastro, pedidos só
     com a janela aberta e por quem lê cadastro, somados aos que já aparecem
     nas medições e na margem — quem não lê cadastro ainda escolhe destes. */
  const podeLerCadastro = permissoes.includes('cadastro.ler');
  const contratosDoCadastro = useCadastro('contratos', podeLerCadastro && montando);
  const opcoesDeContrato = useMemo(() => {
    const porId = new Map<string, string>();
    for (const c of contratosDoCadastro.data ?? []) {
      if (c.ativo) porId.set(c.id, `${c.codigo} · ${c.descricao ?? c.nome ?? ''}`);
    }
    for (const l of [...linhas, ...(margem.data?.linhas ?? [])]) {
      if (!porId.has(l.contratoId)) porId.set(l.contratoId, `${l.contrato} · ${l.cliente}`);
    }
    return [...porId].map(([valor, rotulo]) => ({ valor, rotulo })).sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
  }, [contratosDoCadastro.data, linhas, margem.data]);

  /* O PRÓXIMO CONTRATO A MEDIR (31/10/2026): o já medido antes que ainda
     não tem medição viva na competência escolhida. */
  const aMedir = useMemo(() => {
    const proposta = contratoAMedir(linhas, competencia);
    return proposta !== null && opcoesDeContrato.some((o) => o.valor === proposta.contratoId) ? proposta : null;
  }, [linhas, competencia, opcoesDeContrato]);
  const contratoProposto = aMedir?.contratoId ?? '';
  useEffect(() => {
    if (!montando || contratoTocado) return;
    setContratoId(contratoProposto);
  }, [montando, contratoTocado, contratoProposto]);

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['receita'] });
  const fecharPendente = () => {
    setPendente(null);
    setMotivo('');
    setNumero('');
    setValorRecebido('');
    setContingencia('');
    emitir.reset();
    receber.reset();
  };

  /* O "em quê" do retorno (R04, p.32): o contrato e a competência do boletim. */
  const doBoletim = (id: string) => {
    const m = linhas.find((x) => x.id === id || x.documentoId === id);
    return m ? { alvo: { codigo: formatarCompetencia(m.competencia), nome: `${m.contrato} · ${m.cliente}` }, medicao: m } : { medicao: undefined };
  };

  const montar = useMutation({
    mutationFn: () => api.montar(contratoId, competencia),
    meta: {
      retorno: retornoDe<{ itens: number }>((r) => ({
        titulo: 'Boletim de medição montado',
        alvo: { codigo: formatarCompetencia(competencia) },
        fatos: [`${formatarInteiro(r.itens)} ${r.itens === 1 ? 'item' : 'itens'}`],
        efeito: 'Fica em proposta até alguém aprovar',
      })),
    },
    onSuccess: async () => {
      setMontando(false);
      setContratoId('');
      setContratoTocado(false);
      await recarregar();
    },
  });
  const aprovar = useMutation({
    mutationFn: api.aprovar,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const { alvo, medicao } = doBoletim(id);
        return { titulo: 'Boletim de medição aprovado', ...(alvo ? { alvo } : {}), fatos: [medicao ? formatarReais(medicao.valor) : null] };
      }),
    },
    onSuccess: recarregar,
  });
  const cancelar = useMutation({
    mutationFn: (p: { id: string; motivo: string }) => api.cancelar(p.id, p.motivo),
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string }>((_r, p) => {
        const { alvo, medicao } = doBoletim(p.id);
        return {
          titulo: 'Boletim de medição cancelado',
          ...(alvo ? { alvo } : {}),
          fatos: [medicao ? formatarReais(medicao.valor) : null, `motivo: ${p.motivo}`],
        };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  const emitir = useMutation({
    mutationFn: (p: { id: string; numero: string; quando: string; contingencia?: string }) => api.emitir(p.id, p.numero, p.quando, p.contingencia),
    meta: {
      retorno: retornoDe<{ emContingencia?: boolean }, { id: string; numero: string; quando: string; contingencia?: string }>((r, p) => {
        const { alvo, medicao } = doBoletim(p.id);
        return {
          titulo: r.emContingencia ? 'Documento de cobrança emitido em contingência' : 'Documento de cobrança emitido',
          ...(r.emContingencia ? { tom: 'ressalva' as const, efeito: EFEITO_DA_CONTINGENCIA } : {}),
          ...(alvo ? { alvo } : {}),
          fatos: [`número ${p.numero}`, medicao ? formatarReais(medicao.valor) : null, r.emContingencia && p.contingencia ? `motivo: ${p.contingencia}` : null],
          codigo: p.numero,
        };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  const receber = useMutation({
    mutationFn: (p: { documentoId: string; quando: string; valor: number; contingencia?: string }) =>
      api.receber(p.documentoId, p.quando, p.valor, p.contingencia),
    meta: {
      retorno: retornoDe<{ emContingencia?: boolean }, { documentoId: string; valor: number; contingencia?: string }>((r, p) => {
        const { alvo, medicao } = doBoletim(p.documentoId);
        const diferenca = medicao ? p.valor - medicao.valor : 0;
        const efeitos = [
          r.emContingencia ? EFEITO_DA_CONTINGENCIA : null,
          medicao && diferenca !== 0 ? `Diferença de ${formatarReais(diferenca)} para o valor do boletim` : null,
        ].filter((x): x is string => x !== null);
        return {
          titulo: r.emContingencia ? 'Recebimento registrado em contingência' : 'Recebimento registrado',
          tom: r.emContingencia || (medicao && diferenca !== 0) ? 'ressalva' : 'sucesso',
          ...(alvo ? { alvo } : {}),
          fatos: [`${formatarReais(p.valor)} recebidos`, medicao?.documento ? `documento ${medicao.documento}` : null, r.emContingencia && p.contingencia ? `motivo: ${p.contingencia}` : null],
          ...(efeitos.length > 0 ? { efeito: efeitos.join('. ') } : {}),
        };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });

  /* 0109: a recusa da regra I6 (o ERP é o mestre da cobrança) abre o campo da contingência. */
  const recusaDaEmissao = emitir.isError ? recusaDoMestre(emitir.error) : null;
  const recusaDoRecebimento = receber.isError ? recusaDoMestre(receber.error) : null;

  const reais = (v: number) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(v)}</span>;

  /**
   * A linha tem duas informações (p.18): o contrato em cima, e embaixo o
   * cliente com o ESTADO DO FATURAMENTO daquela medição — "NF-51 · recebido em
   * 10/09/2026". Documento e recebimento moram aqui, e não em colunas de
   * valor: a lista toda poderia ser somada, e seria o erro do v6.
   */
  const COLUNAS: readonly Coluna<Medicao>[] = [
    {
      chave: 'competencia',
      rotulo: 'Competência',
      celula: (m) => <span className="codigo text-tinta">{formatarCompetencia(m.competencia)}</span>,
    },
    {
      chave: 'contrato',
      rotulo: 'Contrato',
      celula: (m) => {
        const faturamento = m.documento === null
          ? 'sem documento emitido'
          : m.recebidoEm === null
            ? `${m.documento} · a receber`
            : `${m.documento} · recebido`;
        return (
          <span className="flex min-w-0 flex-col">
            <span className="codigo text-tinta">{m.contrato}</span>
            <TextoDaCelula texto={`${m.cliente} · ${faturamento}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
          </span>
        );
      },
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      historia: 'SEL-REC-02',
      /* Texto escrito, e não selo: os seis status do manual descrevem uma
         máquina, e o porquê inteiro está em `situacaoEscrita`. */
      celula: (m) => <span className="txt-corpo text-tinta">{situacaoEscrita(m.situacao)}</span>,
    },
    {
      chave: 'equipamentos',
      rotulo: 'Equipamentos',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (m) => <span className="numero text-tinta">{formatarInteiro(m.equipamentos)}</span>,
    },
    {
      chave: 'horas',
      rotulo: 'Horas reconhecidas',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (m) => (
        <span className="whitespace-nowrap">
          <Medida valor={formatarDecimal(m.horasReconhecidas, 1)} unidade="h" />
        </span>
      ),
    },
    {
      chave: 'valor',
      rotulo: 'Valor da medição',
      alinhamento: 'numero',
      celula: (m) => reais(m.valor),
      /* O rodapé soma SÓ o aprovado, igual à faixa. Uma soma da coluna inteira
         misturaria o aceito com o proposto. */
      total: () => reais(totais.receitaAprovada),
    },
  ];

  const acoesDaLinha = (m: Medicao): readonly AcaoDoMenu[] => {
    const monte = (
      tipo: 'aprovar' | 'cancelar' | 'emitir' | 'receber',
      rotulo: string,
      aoEscolher: () => void,
    ): AcaoDoMenu => {
      const motivoIndisponivel = porqueNaoDa(m, tipo, permissoes);
      return motivoIndisponivel === undefined ? { rotulo, aoEscolher } : { rotulo, aoEscolher, motivoIndisponivel };
    };
    return [
      ...(podeAprovarMedicao ? [monte('aprovar', 'Aprovar medição', () => aprovar.mutate(m.id))] : []),
      ...(podeEmitir ? [monte('emitir', 'Emitir documento', () => setPendente({ tipo: 'emitir', medicao: m }))] : []),
      ...(podeReceber
        ? [
            monte('receber', 'Registrar recebimento', () => {
              /* O valor proposto é o da medição: o cliente que paga o boletim
                 inteiro é o caso comum, e a diferença continua a um campo. */
              setValorRecebido(valorDoCampo(m.valor));
              setPendente({ tipo: 'receber', medicao: m });
            }),
          ]
        : []),
      ...(podeAprovarMedicao ? [monte('cancelar', 'Cancelar medição', () => setPendente({ tipo: 'cancelar', medicao: m }))] : []),
    ];
  };

  const aoMontar = (evento: FormEvent) => {
    evento.preventDefault();
    montar.mutate();
  };

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Contratos e receita"
          titulo="Medições do contrato"
          subtitulo="As horas reconhecidas do mês com o valor da regra vigente, e o aceite que as transforma em receita"
          acoes={
            podeMontar ? (
            <HistoriaNaMesa codigo="BTN-REC-01" className="flex">
              <JanelaDeAcao
                rotulo="Montar medição"
                titulo="Montar a medição de um contrato"
                nivel="primario"
                aberta={montando}
                aoAbrir={() => setMontando(true)}
                aoFechar={() => setMontando(false)}
                acoes={
                  <>
                    <Botao nivel="terciario" onClick={() => setMontando(false)}>
                      Cancelar
                    </Botao>
                    <Botao nivel="primario" type="submit" form={`${base}-montar`} disabled={montar.isPending}>
                      {montar.isPending ? 'Montando…' : 'Montar medição'}
                    </Botao>
                  </>
                }
              >
                <form id={`${base}-montar`} onSubmit={aoMontar} className="flex flex-col gap-4">
                  <p className="txt-apoio text-tinta-secundaria">
                    A medição é montada a partir da alocação do período, das horas que a conciliação
                    reconheceu e da regra vigente do contrato. Ela nasce em aberto: ninguém a aceitou
                    ainda, e ela não entra na receita até ser aprovada.
                  </p>
                  <CampoSelecao
                    rotulo="Contrato"
                    valor={contratoId}
                    aoMudar={(valor) => {
                      setContratoId(valor);
                      setContratoTocado(true);
                    }}
                    {...(!contratoTocado && aMedir !== null && contratoId === aMedir.contratoId
                      ? { ajuda: `Medido até ${formatarCompetencia(aMedir.ultimaCompetencia)}, ainda sem a medição desta competência.` }
                      : {})}
                    opcoes={opcoesDeContrato}
                    rotuloVazio={rotuloDaEscolha({
                      /* Com contrato à vista nas medições, a escolha já serve enquanto o cadastro chega. */
                      consulta: podeLerCadastro && opcoesDeContrato.length === 0 ? contratosDoCadastro : { isPending: false, isError: false },
                      pode: true,
                      quantas: opcoesDeContrato.length,
                      escolha: 'Escolha o contrato',
                      nenhuma: podeLerCadastro
                        ? 'Nenhum contrato em uso · cadastre em Cadastros › Contratos'
                        : 'Nenhum contrato à vista · o seu perfil não lê os cadastros, peça a liberação em Cadastros › Governança',
                      semPermissao: '',
                    })}
                  />
                  <CampoDeCompetencia rotulo="Competência" valor={competencia} aoMudar={setCompetencia} ajuda="O mês medido." />
                  {montar.isError ? (
                    <p role="alert" className="txt-apoio text-parada-texto">
                      {explicarFalha(montar.error).comoResolver}
                    </p>
                  ) : null}
                </form>
              </JanelaDeAcao>
            </HistoriaNaMesa>
            ) : undefined
          }
        />

        {faixa === 'falhou' ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">As medições não carregaram.</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(medicoes.error).comoResolver}</span>
            </span>
            <Botao nivel="secundario" onClick={() => void medicoes.refetch()}>
              Carregar de novo
            </Botao>
          </div>
        ) : (
          <>
            {/* Carregando (p.32), nunca "R$ 0,00": os quatro cards já se sabem —
                o rótulo e o ícone ficam, e só o número é esqueleto. */}
            <FaixaIndicadores>
              <CardIndicador
                rotulo="Receita aprovada"
                icone={CircleDollarSign}
                tom="marca"
                carregando={faixa === 'carregando'}
                valor={formatarReais(totais.receitaAprovada)}
                contexto="Soma das medições aceitas pelo cliente"
              />
              <CardIndicador
                historia="NUM-REC-03"
                rotulo="Horas reconhecidas"
                icone={Clock}
                tom="neutro"
                carregando={faixa === 'carregando'}
                valor={formatarDecimal(totais.horasReconhecidas, 1)}
                unidade="h"
                contexto="Horas conciliadas que entraram nas medições aprovadas"
              />
              <CardIndicador
                rotulo="A faturar"
                icone={ClipboardList}
                tom="standby"
                carregando={faixa === 'carregando'}
                valor={formatarReais(totais.semDocumento)}
                /* Alerta só quando o próprio valor é o alerta (regra 3). */
                alerta={totais.semDocumento > 0}
                contexto="Aprovado e ainda sem documento emitido"
              />
              <CardIndicador
                rotulo="A receber"
                icone={CircleDollarSign}
                tom="disponivel"
                carregando={faixa === 'carregando'}
                valor={formatarReais(totais.aFaturar)}
                contexto="Documento emitido e recebimento ainda não registrado"
              />
            </FaixaIndicadores>

            {/* De onde o número vem, em toda leitura. */}
            {faixa === 'carregando' ? (
              <Carregando oQue="Carregando as medições">
                <Barra className="my-1 h-3 w-3/5" />
              </Carregando>
            ) : (
              <p className="txt-apoio text-pretty text-tinta-secundaria">{deOndeVemONumero(totais)}</p>
            )}
          </>
        )}

        <Tabela
          rotulo="Medições do contrato"
          colunas={COLUNAS}
          linhas={fatia.visiveis}
          chaveDaLinha={(m) => m.id}
          carregando={medicoes.isPending}
          erro={
            medicoes.isError ? (
              <ErroDaLista oQue="As medições" erro={medicoes.error} aoTentarDeNovo={() => void medicoes.refetch()} />
            ) : null
          }
          alturaDupla
          rotuloDoTotal="Receita aprovada"
          acoes={(m) => (acoesDaLinha(m).length > 0 ? <MaisAcoes acoes={acoesDaLinha(m)} /> : null)}
          vazio={
            <EstadoVazio
              titulo="Nenhuma medição montada"
              descricao="Monte a medição de um contrato para ver as horas reconhecidas do mês com o valor da regra vigente."
              acao={
                permissoes.includes('medicao.gravar') ? (
                  <Botao nivel="secundario" onClick={() => setMontando(true)}>
                    Montar medição
                  </Botao>
                ) : undefined
              }
            />
          }
          rodape={
            <RodapeTabela
              primeiro={fatia.primeiro}
              ultimo={fatia.ultimo}
              total={linhas.length}
              pagina={fatia.pagina}
              paginas={fatia.paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />

        {/* 0096 · a margem por contrato, pela mesma conta da Visão do gestor. */}
        <Tabela
          rotulo="Margem por contrato"
          colunas={COLUNAS_DA_MARGEM}
          linhas={paginaDaMargem.visiveis}
          chaveDaLinha={(l) => `${l.contratoId}-${l.competencia}`}
          carregando={margem.isPending}
          alturaDupla
          erro={margem.isError ? <ErroDaLista oQue="A margem por contrato" erro={margem.error} aoTentarDeNovo={() => void margem.refetch()} /> : null}
          vazio={
            <EstadoVazio
              titulo="Nenhuma medição aprovada"
              descricao="A margem cruza a receita da medição aprovada com o custo total do contrato. Aprove a medição do mês para ela aparecer aqui."
            />
          }
          rodape={<RodapeTabela {...paginaDaMargem.rodape} />}
        />

        {/* A limitação viaja com o número, e a tela a imprime como a rota a
            manda — sem reescrever, para as duas não divergirem. */}
        {margem.isPending ? (
          <Carregando oQue="Carregando a nota sobre a margem" className="flex flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
            <Barra className="h-3 w-2/5" />
          </Carregando>
        ) : margem.data === undefined ? null : (
          <p className="txt-apoio text-pretty text-tinta-secundaria">{margem.data.limitacao}</p>
        )}
      </div>

      {/* As três janelas de decisão curta: ao centro, uma por vez (R01, p.29). */}
      <Janela
        titulo="Cancelar a medição"
        aberta={pendente?.tipo === 'cancelar' && podeAprovarMedicao}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Voltar às medições
            </Botao>
            <Botao
              nivel="secundario"
              disabled={motivo.trim().length < 15 || cancelar.isPending}
              onClick={() => {
                if (pendente?.tipo === 'cancelar') cancelar.mutate({ id: pendente.medicao.id, motivo });
              }}
            >
              {cancelar.isPending ? 'Cancelando…' : 'Cancelar medição'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-corpo text-tinta">
            {pendente?.tipo === 'cancelar'
              ? `Cancelar a medição de ${formatarCompetencia(pendente.medicao.competencia)} do contrato ${pendente.medicao.contrato}? Ela sai da receita e libera a competência para uma medição nova.`
              : ''}
          </p>
          <CampoTexto
            rotulo="Motivo"
            valor={motivo}
            aoMudar={setMotivo}
            ajuda="Pelo menos 15 caracteres. É este texto que explica a medição nova na mesma competência."
          />
          {cancelar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(cancelar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Emitir o documento"
        aberta={pendente?.tipo === 'emitir' && podeEmitir}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Cancelar
            </Botao>
            <HistoriaNaMesa codigo="BTN-REC-04" className="flex">
              <Botao
                nivel="secundario"
                disabled={numero.trim() === '' || emitir.isPending || (recusaDaEmissao !== null && (!podeContingencia || !contingenciaValida(contingencia)))}
                onClick={() => {
                  if (pendente?.tipo === 'emitir') {
                    emitir.mutate({ id: pendente.medicao.id, numero, quando, ...(recusaDaEmissao && podeContingencia ? { contingencia: contingencia.trim() } : {}) });
                  }
                }}
              >
                {emitir.isPending ? 'Emitindo…' : recusaDaEmissao && podeContingencia ? 'Emitir em contingência' : 'Emitir documento'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-apoio text-tinta-secundaria">
            O documento aponta para a medição aprovada e não carrega valor próprio. A receita do mês
            continua sendo a da medição.
          </p>
          <CampoTexto rotulo="Número do documento" valor={numero} aoMudar={setNumero} />
          <CampoDeData rotulo="Emitido em" valor={quando} aoMudar={setQuando} />
          {recusaDaEmissao ? (
            <CampoDaContingencia recusa={recusaDaEmissao} podeDeclarar={podeContingencia} motivo={contingencia} aoMudar={setContingencia} />
          ) : emitir.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(emitir.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Registrar o recebimento"
        aberta={pendente?.tipo === 'receber' && podeReceber}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Cancelar
            </Botao>
            <HistoriaNaMesa codigo="BTN-REC-05" className="flex">
              <Botao
                nivel="secundario"
                disabled={interpretarNumero(valorRecebido) === null || receber.isPending || (recusaDoRecebimento !== null && (!podeContingencia || !contingenciaValida(contingencia)))}
                onClick={() => {
                  const valor = interpretarNumero(valorRecebido);
                  if (pendente?.tipo === 'receber' && pendente.medicao.documentoId !== null && valor !== null) {
                    receber.mutate({ documentoId: pendente.medicao.documentoId, quando, valor, ...(recusaDoRecebimento && podeContingencia ? { contingencia: contingencia.trim() } : {}) });
                  }
                }}
              >
                {receber.isPending ? 'Registrando…' : recusaDoRecebimento && podeContingencia ? 'Registrar em contingência' : 'Registrar recebimento'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {/* O recebimento é uma data NESTE documento, e não uma receita nova.
              A frase está aqui porque é onde a dúvida aparece. */}
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            {pendente?.tipo === 'receber'
              ? `O que entrou pelo documento ${pendente.medicao.documento ?? ''}. Registrar o recebimento não muda a receita do mês: ela continua sendo a da medição aprovada. Recebido a menos aparece na diferença.`
              : ''}
          </p>
          <CampoDeData rotulo="Recebido em" valor={quando} aoMudar={setQuando} />
          <CampoTexto
            rotulo="Valor recebido"
            valor={valorRecebido}
            aoMudar={setValorRecebido}
            ajuda={
              pendente?.tipo === 'receber'
                ? `Veio o valor da medição, ${formatarReais(pendente.medicao.valor)}. Troque se entrou outro valor.`
                : 'Informe o que efetivamente entrou.'
            }
          />
          {recusaDoRecebimento ? (
            <CampoDaContingencia recusa={recusaDoRecebimento} podeDeclarar={podeContingencia} motivo={contingencia} aoMudar={setContingencia} />
          ) : receber.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(receber.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>
    </div>
  );
}

/** As colunas da margem por contrato (0096). A margem diz "parcial" quando algo ficou fora na competência. */
const COLUNAS_DA_MARGEM: readonly Coluna<LinhaDeMargem>[] = [
  {
    chave: 'contrato',
    rotulo: 'Contrato',
    celula: (l) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{l.contrato}</span>
        <TextoDaCelula texto={l.cliente} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  { chave: 'competencia', rotulo: 'Competência', celula: (l) => <span className="numero text-tinta">{formatarCompetencia(l.competencia)}</span> },
  {
    chave: 'receita',
    rotulo: 'Receita aprovada',
    alinhamento: 'numero',
    celula: (l) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(Number(l.receita))}</span>,
  },
  {
    chave: 'custo',
    rotulo: 'Custo total',
    alinhamento: 'numero',
    celula: (l) =>
      l.custoTotal === undefined || l.custoTotal === null ? (
        <span className="text-tinta-secundaria">—</span>
      ) : (
        <span className="numero whitespace-nowrap text-tinta">{formatarReais(l.custoTotal)}</span>
      ),
  },
  {
    chave: 'margem',
    rotulo: 'Margem',
    alinhamento: 'numero',
    /* Valor em preto; vermelho só quando o próprio valor é o alerta. "parcial"
       escrito embaixo quando algo ficou fora na competência. */
    celula: (l) =>
      l.margem === undefined || l.margem === null ? (
        <span className="txt-apoio text-tinta-secundaria">{l.custoTotal === null || l.custoTotal === undefined ? 'sem custo apurado' : margemEscrita(null)}</span>
      ) : (
        <span className="flex flex-col items-end">
          <span className={`numero whitespace-nowrap ${l.margem < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{margemEscrita(l.margem)}</span>
          {l.completo ? null : <span className="txt-apoio whitespace-nowrap text-tinta-apagada">parcial</span>}
        </span>
      ),
  },
];
